// Hierarchy Data Propagation Page (REQ-BM-030–033)
// Lists assignments and allows creating assignments (users at scope, due date, process filter, optional action).

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import api from '@/services/api';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  UsersIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface AssignmentRow {
  id: string;
  benchmark_id: string;
  assignee_id: string;
  assigned_by_id: string | null;
  due_date: string | null;
  process_filter_ids: string[] | null;
  status: string;
  created_at: string;
  assignee_email?: string;
}

interface UserAtScope {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface HierarchyInput {
  id: string;
  benchmarkId: string;
  submittedBy: {
    id: string;
    name: string;
    email: string;
    hierarchyLevel: string;
    businessUnit: string;
    function: string;
    location: string;
  };
  submittedAt: string;
  status: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'PROPAGATED';
  data: {
    location: string;
    function_name: string;
    business: string;
    cost_amount: number;
    fte_count: number;
    footprint_sq_ft: number;
    cost_bucket: string;
  };
  reviewNotes?: string;
  rejectionReason?: string;
  approvedBy?: string;
  approvedAt?: string;
}

interface AggregatedData {
  totalCost: number;
  totalFTE: number;
  totalFootprint: number;
  costByBucket: Record<string, number>;
  costByFunction: Record<string, number>;
  costByLocation: Record<string, number>;
  submissionCount: number;
  approvalRate: number;
}

export const HierarchyPropagationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [inputs, setInputs] = useState<HierarchyInput[]>([]);
  const [aggregatedData, setAggregatedData] = useState<AggregatedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedInputs, setSelectedInputs] = useState<string[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('submittedAt');

  // Assignments (real API)
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [showCreateAssignmentModal, setShowCreateAssignmentModal] = useState(false);
  const [usersAtScope, setUsersAtScope] = useState<UserAtScope[]>([]);
  const [processes, setProcesses] = useState<{ id: string; name: string }[]>([]);
  const [createAssigneeId, setCreateAssigneeId] = useState('');
  const [createDueDate, setCreateDueDate] = useState('');
  const [createProcessFilterIds, setCreateProcessFilterIds] = useState<string[]>([]);
  const [createAction, setCreateAction] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (id) {
      loadAssignments();
      loadHierarchyInputs();
      loadAggregatedData();
    }
  }, [id]);

  const loadAssignments = async () => {
    if (!id) return;
    try {
      const r = await api.get(`/benchmarking/${id}/assignments`);
      setAssignments(r.data?.data ?? r.data ?? []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load assignments');
      setAssignments([]);
    }
  };

  const openCreateAssignment = async () => {
    if (!id) return;
    setCreateAssigneeId('');
    setCreateDueDate('');
    setCreateProcessFilterIds([]);
    setCreateAction(true);
    setShowCreateAssignmentModal(true);
    try {
      const [usersRes, processesRes] = await Promise.all([
        api.get(`/benchmarking/${id}/users-at-scope`),
        api.get('/admin/process-admin/processes', { params: { includeSubprocesses: 'true' } })
      ]);
      setUsersAtScope(usersRes.data?.data ?? usersRes.data ?? []);
      const list = processesRes.data?.data ?? processesRes.data ?? [];
      setProcesses(list.filter((p: { parent_id: string | null }) => !p.parent_id).map((p: { id: string; name: string }) => ({ id: p.id, name: p.name })));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load users or processes');
      setUsersAtScope([]);
      setProcesses([]);
    }
  };

  const submitCreateAssignment = async () => {
    if (!id || !createAssigneeId) {
      toast.error('Please select an assignee');
      return;
    }
    setCreating(true);
    try {
      await api.post(`/benchmarking/${id}/assignments`, {
        assigneeId: createAssigneeId,
        dueDate: createDueDate || undefined,
        processFilterIds: createProcessFilterIds.length ? createProcessFilterIds : undefined,
        createAction
      });
      toast.success('Assignment created');
      setShowCreateAssignmentModal(false);
      loadAssignments();
    } catch (e) {
      console.error(e);
      toast.error('Failed to create assignment');
    } finally {
      setCreating(false);
    }
  };

  const loadHierarchyInputs = async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      const mockInputs: HierarchyInput[] = [
        {
          id: '1',
          benchmarkId: id!,
          submittedBy: {
            id: 'user1',
            name: 'John Smith',
            email: 'john.smith@company.com',
            hierarchyLevel: 'Manager',
            businessUnit: 'Operations',
            function: 'Finance',
            location: 'New York'
          },
          submittedAt: '2024-01-15T10:30:00Z',
          status: 'PENDING_REVIEW',
          data: {
            location: 'New York',
            function_name: 'Finance',
            business: 'Operations',
            cost_amount: 150000,
            fte_count: 5,
            footprint_sq_ft: 2000,
            cost_bucket: 'Labor'
          }
        },
        {
          id: '2',
          benchmarkId: id!,
          submittedBy: {
            id: 'user2',
            name: 'Sarah Johnson',
            email: 'sarah.johnson@company.com',
            hierarchyLevel: 'Senior Manager',
            businessUnit: 'Technology',
            function: 'IT',
            location: 'San Francisco'
          },
          submittedAt: '2024-01-14T14:20:00Z',
          status: 'PENDING_REVIEW',
          data: {
            location: 'San Francisco',
            function_name: 'IT',
            business: 'Technology',
            cost_amount: 200000,
            fte_count: 8,
            footprint_sq_ft: 3000,
            cost_bucket: 'Technology'
          }
        },
        {
          id: '3',
          benchmarkId: id!,
          submittedBy: {
            id: 'user3',
            name: 'Mike Davis',
            email: 'mike.davis@company.com',
            hierarchyLevel: 'Director',
            businessUnit: 'Sales',
            function: 'Sales',
            location: 'Chicago'
          },
          submittedAt: '2024-01-13T09:15:00Z',
          status: 'APPROVED',
          data: {
            location: 'Chicago',
            function_name: 'Sales',
            business: 'Sales',
            cost_amount: 180000,
            fte_count: 6,
            footprint_sq_ft: 2500,
            cost_bucket: 'Labor'
          },
          approvedBy: 'Jane Wilson',
          approvedAt: '2024-01-14T16:45:00Z'
        }
      ];
      
      setInputs(mockInputs);
    } catch (error) {
      console.error('Error loading hierarchy inputs:', error);
      toast.error('Failed to load hierarchy inputs');
    } finally {
      setLoading(false);
    }
  };

  const loadAggregatedData = async () => {
    try {
      // Mock aggregated data - replace with actual API call
      const mockAggregated: AggregatedData = {
        totalCost: 530000,
        totalFTE: 19,
        totalFootprint: 7500,
        costByBucket: {
          'Labor': 330000,
          'Technology': 200000
        },
        costByFunction: {
          'Finance': 150000,
          'IT': 200000,
          'Sales': 180000
        },
        costByLocation: {
          'New York': 150000,
          'San Francisco': 200000,
          'Chicago': 180000
        },
        submissionCount: 3,
        approvalRate: 33.33
      };
      
      setAggregatedData(mockAggregated);
    } catch (error) {
      console.error('Error loading aggregated data:', error);
    }
  };

  const handleSelectInput = (inputId: string) => {
    setSelectedInputs(prev => 
      prev.includes(inputId) 
        ? prev.filter(id => id !== inputId)
        : [...prev, inputId]
    );
  };

  const handleSelectAll = () => {
    const pendingInputs = filteredInputs
      .filter(input => input.status === 'PENDING_REVIEW')
      .map(input => input.id);
    setSelectedInputs(pendingInputs);
  };

  const handleApproveSelected = async () => {
    if (selectedInputs.length === 0) {
      toast.error('Please select inputs to approve');
      return;
    }

    try {
      setLoading(true);
      // API call to approve selected inputs
            
      // Update local state
      setInputs(prev => prev.map(input => 
        selectedInputs.includes(input.id) 
          ? { ...input, status: 'APPROVED' as const, reviewNotes, approvedBy: user?.email || 'Current User', approvedAt: new Date().toISOString() }
          : input
      ));
      
      setSelectedInputs([]);
      setShowReviewModal(false);
      setReviewNotes('');
      toast.success(`${selectedInputs.length} inputs approved successfully`);
    } catch (error) {
      console.error('Error approving inputs:', error);
      toast.error('Failed to approve inputs');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedInputs.length === 0) {
      toast.error('Please select inputs to reject');
      return;
    }

    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setLoading(true);
      // API call to reject selected inputs
            
      // Update local state
      setInputs(prev => prev.map(input => 
        selectedInputs.includes(input.id) 
          ? { ...input, status: 'REJECTED' as const, rejectionReason }
          : input
      ));
      
      setSelectedInputs([]);
      setRejectionReason('');
      toast.success(`${selectedInputs.length} inputs rejected`);
    } catch (error) {
      console.error('Error rejecting inputs:', error);
      toast.error('Failed to reject inputs');
    } finally {
      setLoading(false);
    }
  };

  const handlePropagateUpward = async () => {
    const approvedInputs = inputs.filter(input => input.status === 'APPROVED');
    if (approvedInputs.length === 0) {
      toast.error('No approved inputs to propagate');
      return;
    }

    try {
      setLoading(true);
      // API call to propagate data upward
            
      // Update local state
      setInputs(prev => prev.map(input => 
        input.status === 'APPROVED' 
          ? { ...input, status: 'PROPAGATED' as const }
          : input
      ));
      
      toast.success('Data propagated upward successfully');
    } catch (error) {
      console.error('Error propagating data:', error);
      toast.error('Failed to propagate data');
    } finally {
      setLoading(false);
    }
  };

  const filteredInputs = inputs.filter(input => 
    filterStatus === 'all' || input.status === filterStatus
  );

  const sortedInputs = [...filteredInputs].sort((a, b) => {
    switch (sortBy) {
      case 'submittedAt':
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
      case 'submittedBy':
        return a.submittedBy.name.localeCompare(b.submittedBy.name);
      case 'costAmount':
        return b.data.cost_amount - a.data.cost_amount;
      default:
        return 0;
    }
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING_REVIEW':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'REJECTED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'PROPAGATED':
        return <ArrowUpIcon className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_REVIEW':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PROPAGATED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="hierarchy-propagation-page">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/benchmarking/${id}`)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowDownIcon className="h-4 w-4 mr-2" />
                {t('common.back')}
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('benchmarking.hierarchyPropagation') || 'Hierarchy Data Propagation'}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {t('benchmarking.hierarchyPropagationDescription') || 'Review and propagate data from users below in hierarchy'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={openCreateAssignment}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create assignment
              </button>
              <button
                onClick={handlePropagateUpward}
                disabled={inputs.filter(input => input.status === 'APPROVED').length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <ArrowUpIcon className="h-4 w-4 mr-2" />
                {t('benchmarking.propagateUpward') || 'Propagate Upward'}
              </button>
            </div>
          </div>

          {/* Assignments list (REQ-BM-030, REQ-BM-031) */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assignments</h3>
              <p className="text-sm text-gray-500 mt-1">Assignees and due dates for this benchmark.</p>
            </div>
            <div className="overflow-x-auto">
              {assignments.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">No assignments yet. Create one to get started.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assignments.map((a) => (
                      <tr key={a.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{a.assignee_email ?? a.assignee_id}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{a.due_date ? new Date(a.due_date).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            a.status === 'submitted' ? 'bg-green-100 text-green-800' :
                            a.status === 'returned_for_revision' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{new Date(a.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          {aggregatedData && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UsersIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('benchmarking.totalSubmissions') || 'Total Submissions'}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {aggregatedData.submissionCount}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('benchmarking.totalCost') || 'Total Cost'}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          ${aggregatedData.totalCost.toLocaleString()}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UsersIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('benchmarking.totalFTE') || 'Total FTE'}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {aggregatedData.totalFTE}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('benchmarking.approvalRate') || 'Approval Rate'}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {aggregatedData.approvalRate.toFixed(1)}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('common.filterBy') || 'Filter by Status'}
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="all">{t('common.all') || 'All'}</option>
                    <option value="PENDING_REVIEW">{t('benchmarking.pendingReview') || 'Pending Review'}</option>
                    <option value="APPROVED">{t('benchmarking.approved') || 'Approved'}</option>
                    <option value="REJECTED">{t('benchmarking.rejected') || 'Rejected'}</option>
                    <option value="PROPAGATED">{t('benchmarking.propagated') || 'Propagated'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('common.sortBy') || 'Sort by'}
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="submittedAt">{t('benchmarking.submittedAt') || 'Submitted At'}</option>
                    <option value="submittedBy">{t('benchmarking.submittedBy') || 'Submitted By'}</option>
                    <option value="costAmount">{t('benchmarking.costAmount') || 'Cost Amount'}</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('common.selectAll') || 'Select All Pending'}
                </button>
                <button
                  onClick={() => setShowReviewModal(true)}
                  disabled={selectedInputs.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {t('benchmarking.approveSelected') || 'Approve Selected'}
                </button>
                <button
                  onClick={() => {
                    setShowReviewModal(true);
                    setRejectionReason('');
                  }}
                  disabled={selectedInputs.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  {t('benchmarking.rejectSelected') || 'Reject Selected'}
                </button>
              </div>
            </div>
          </div>

          {/* Inputs Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t('benchmarking.hierarchyInputs') || 'Hierarchy Inputs'}
              </h3>
              
              {sortedInputs.length === 0 ? (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {t('benchmarking.noInputsFound') || 'No inputs found'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {t('benchmarking.noInputsDescription') || 'No hierarchy inputs match your current filters.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedInputs.length === sortedInputs.filter(input => input.status === 'PENDING_REVIEW').length && selectedInputs.length > 0}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('benchmarking.submittedBy') || 'Submitted By'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('benchmarking.data') || 'Data'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('benchmarking.costAmount') || 'Cost Amount'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('benchmarking.status') || 'Status'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('benchmarking.submittedAt') || 'Submitted At'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('common.actions') || 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedInputs.map((input) => (
                        <tr key={input.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedInputs.includes(input.id)}
                              onChange={() => handleSelectInput(input.id)}
                              disabled={input.status !== 'PENDING_REVIEW'}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-700">
                                    {input.submittedBy.name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {input.submittedBy.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {input.submittedBy.email}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {input.submittedBy.hierarchyLevel} • {input.submittedBy.businessUnit}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">{input.data.function_name}</div>
                              <div className="text-gray-500">{input.data.location}</div>
                              <div className="text-gray-500">{input.data.business}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">${input.data.cost_amount.toLocaleString()}</div>
                              <div className="text-gray-500">{input.data.fte_count} FTE</div>
                              <div className="text-gray-500">{input.data.footprint_sq_ft?.toLocaleString()} sq ft</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(input.status)}
                              <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(input.status)}`}>
                                {input.status.replace('_', ' ')}
                              </span>
                            </div>
                            {input.reviewNotes && (
                              <div className="text-xs text-gray-500 mt-1">
                                Notes: {input.reviewNotes}
                              </div>
                            )}
                            {input.rejectionReason && (
                              <div className="text-xs text-red-500 mt-1">
                                Reason: {input.rejectionReason}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(input.submittedAt).toLocaleDateString()} {new Date(input.submittedAt).toLocaleTimeString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => console.log('View details for:', input.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Assignment Modal (REQ-BM-030, REQ-BM-031, REQ-BM-032) */}
      {showCreateAssignmentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create assignment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee *</label>
                <select
                  value={createAssigneeId}
                  onChange={(e) => setCreateAssigneeId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select user at scope</option>
                  {usersAtScope.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name || u.last_name ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : u.email}
                      {u.email ? ` (${u.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
                <input
                  type="date"
                  value={createDueDate}
                  onChange={(e) => setCreateDueDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              {processes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Process filter (optional)</label>
                  <select
                    multiple
                    value={createProcessFilterIds}
                    onChange={(e) => setCreateProcessFilterIds(Array.from(e.target.selectedOptions, (o) => o.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    {processes.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple.</p>
                </div>
              )}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="createAction"
                  checked={createAction}
                  onChange={(e) => setCreateAction(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="createAction" className="ml-2 text-sm text-gray-700">Create action/notification for assignee</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateAssignmentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateAssignment}
                disabled={creating || !createAssigneeId}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('benchmarking.reviewSelectedInputs') || 'Review Selected Inputs'}
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('benchmarking.reviewNotes') || 'Review Notes (Optional)'}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any notes about your review..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('benchmarking.rejectionReason') || 'Rejection Reason (If Rejecting)'}
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide reason for rejection..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setReviewNotes('');
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleApproveSelected}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2 inline" />
                  {t('benchmarking.approve') || 'Approve'}
                </button>
                <button
                  onClick={handleRejectSelected}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <XCircleIcon className="h-4 w-4 mr-2 inline" />
                  {t('benchmarking.reject') || 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
